import React, { useEffect, useState } from 'react';
import AsCalendarV from './AsCalendar.v';
import { withRouter } from 'core/withRouter';
import PageUtil from 'utils/Page.util';
import { dateFormat } from 'utils/Date.util';
import receptionService from 'services/reception.service';
import { ReceptionDTO, ReceptionSearchDTO } from 'models/reception';
import { DateType, ReceptionStatus, ReceptionType } from 'enums/reception.enum';
import moment from 'moment';
import ReceptionModal from 'components/_common/modal/ReceptionModal';

/** 내부 상태 모델 */
class Params {
    error: string | null = null;
    isLoaded = false;

    // 출장일자 미정 목록
    undecidedList: ReceptionDTO[] = [];

    // 캘린더에 표시될 출장 확정 목록
    calendarList: ReceptionDTO[] = [];

    // 캘린더 선택된 날짜
    selectedDate: string | null = null;

    // 현재 보고 있는 년월
    currentYear: number = new Date().getFullYear();
    currentMonth: number = new Date().getMonth() + 1; // 1~12
}

function AsCalendar() {
    const [params, setParams] = useState(new Params());

    // 모달 상태 관리
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [editingRow, setEditingRow] = useState<ReceptionDTO | null>(null);

    /** 초기 진입 시 데이터 로드 */
    useEffect(() => {
        loadUndecidedList();
        loadCalendarData(params.currentYear, params.currentMonth);
    }, []);

    /** 출장일자 미정 목록 조회 */
    async function loadUndecidedList() {
        try {
            const search = new ReceptionSearchDTO();
            search.ignoreStatus = [ReceptionStatus.DONE, ReceptionStatus.PAID].join(",");
            search.types = [ReceptionType.TRIP].join(",");
            search.page = 1;
            search.rows = 9999;
            // 백엔드에서 tripDate가 null인 경우를 필터링하는 로직 필요
            // 또는 전체 조회 후 프론트에서 필터링

            const res = await receptionService.list(search);

            // tripCard가 없거나 tripDate가 null인 항목만 필터링
            const undecided = (res.content || []).filter(
                item => !item.tripCard?.tripDate
            );

            setParams((prev) => ({
                ...prev,
                undecidedList: undecided,
                isLoaded: true,
                error: null,
            }));
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                isLoaded: true,
                error: error as any,
            }));
            PageUtil.handleApiError(error);
        }
    }

    /** 캘린더 데이터 조회 (특정 년월) */
    async function loadCalendarData(year: number, month: number) {
        try {
            const search = new ReceptionSearchDTO();
            // 해당 월의 첫날부터 마지막날까지
            const startDate = moment(`${year}-${String(month).padStart(2, '0')}-01`);
            const endDate = startDate.clone().endOf('month');

            search.startDate = startDate.format('YYYY-MM-DD');
            search.endDate = endDate.format('YYYY-MM-DD');
            search.dateType = DateType.TRIP;
            // search.ignoreStatus = [ReceptionStatus.DONE, ReceptionStatus.PAID].join(",");
            search.page = 1;
            search.rows = 9999;

            const res = await receptionService.list(search);

            setParams((prev) => ({
                ...prev,
                calendarList: res.content || [],
                isLoaded: true,
                error: null,
            }));
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                isLoaded: true,
                error: error as any,
            }));
            PageUtil.handleApiError(error);
        }
    }

    /** 월 변경 (이전/다음) */
    function onChangeMonth(direction: 'prev' | 'next') {
        const cur = PageUtil.deepCopy(params) as Params;

        if (direction === 'prev') {
            if (cur.currentMonth === 1) {
                cur.currentYear -= 1;
                cur.currentMonth = 12;
            } else {
                cur.currentMonth -= 1;
            }
        } else {
            if (cur.currentMonth === 12) {
                cur.currentYear += 1;
                cur.currentMonth = 1;
            } else {
                cur.currentMonth += 1;
            }
        }

        setParams(cur);
        loadCalendarData(cur.currentYear, cur.currentMonth);
    }

    /** 캘린더 날짜 클릭 */
    function onSelectDate(date: string) {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.selectedDate = date;
        setParams(cur);
    }

    /** 출장일자 미정 항목을 캘린더로 드래그 앤 드롭 */
    async function onDropToCalendar(reception: ReceptionDTO, targetDate: string) {
        if (!confirm(`${reception.tripCard?.agent || '출장자 미정'}님의 출장일자를 ${targetDate}로 지정하시겠습니까?`)) {
            return;
        }

        try {
            // tripCard가 없으면 생성
            if (!reception.tripCard) {
                reception.tripCard = {
                    tripDate: targetDate,
                };
            } else {
                reception.tripCard.tripDate = targetDate;
            }

            await receptionService.save(reception);

            // 데이터 새로고침
            loadUndecidedList();
            loadCalendarData(params.currentYear, params.currentMonth);

            alert('출장일자가 지정되었습니다.');
        } catch (error) {
            PageUtil.handleApiError(error);
        }
    }

    /** 신규 접수 모달 열기 */
    function onOpenCreate() {
        setModalMode('create');
        setEditingRow(null);
        setShowModal(true);
    }

    /** 수정 모달 열기 */
    function onOpenEdit(row: ReceptionDTO) {
        setModalMode('edit');
        setEditingRow(row);
        setShowModal(true);
    }

    /** 모달에서 저장 완료 시 */
    async function onModalSave(reception: ReceptionDTO, close: boolean = true) {
        try {
            console.log("reception", reception);
            await receptionService.save(reception);
            alert('저장되었습니다.');
            if (close) {
                setShowModal(false);
                location.reload();
            }
        } catch (error) {
            console.error('접수 저장 실패:', error);
            alert('저장에 실패했습니다.');
        }
    }

    /** 모달 닫기 */
    function onModalClose() {
        setShowModal(false);
        setEditingRow(null);
    }

    const args = {
        params,
        setParams,

        onChangeMonth,
        onSelectDate,
        onDropToCalendar,
        onOpenCreate,
        onOpenEdit,
    };

    return <>

        <AsCalendarV {...args} />

        {/* 접수 등록/수정 모달 */}
        <ReceptionModal
            show={showModal}
            title={modalMode === 'create' ? '신규 접수 등록' : '접수 수정'}
            initial={editingRow}
            onSave={onModalSave}
            onClose={onModalClose}
        />

    </>

}

export default withRouter(AsCalendar);